<template>
  <div class="qa-graded-call" data-test="qa-graded-call">
    <!-- The verdict, its arithmetic, and what a coach would say — before the question-by-question detail, which is
         the evidence for it. -->
    <div style="padding: 14px 16px; border-bottom: 1px solid #E8E9E9; background: #FCFCFD;">
      <div style="display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;">
        <span
          style="font-size: 1.85em; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums;"
          :style="{ color: scoreColour(grade.score_pct) }"
          data-test="qa-graded-score"
        >{{ fmtPct(grade.score_pct) }}</span>
        <span style="color: #6B7280; font-variant-numeric: tabular-nums;">
          {{ grade.earned }} of {{ grade.applicable }} applicable points
        </span>
        <span v-if="grade.failed" class="qa-tag qa-tag-fail">auto-fail</span>
        <span style="margin-left: auto; color: #A5A5A6;" data-test="qa-graded-tally">{{ answerTally }}</span>
      </div>

      <p v-if="grade.summary" style="color: #353537; line-height: 1.55; margin: 10px 0 0; max-width: 860px;">{{ grade.summary }}</p>

      <div v-if="grade.source === 'human'" style="display: flex; flex-wrap: wrap; gap: 4px 16px; margin-top: 9px; color: #6B7280;">
        <span>Audit ID {{ grade.analyst_job_id }}</span>
        <span v-if="grade.auditor_name">Audited by {{ grade.auditor_name }}</span>
        <span v-if="grade.reviewed_opportunity_id">Opportunity reviewed: {{ grade.reviewed_opportunity_type || 'Opportunity' }} (#{{ grade.reviewed_opportunity_id }})</span>
      </div>
      <div v-if="grade.review_notes" style="margin-top: 9px; max-width: 860px; color: #353537; line-height: 1.5; white-space: pre-wrap;"><span style="font-weight: 600;">Review notes:</span> {{ grade.review_notes }}</div>

      <div v-if="grade.coaching" style="display: flex; gap: 8px; margin-top: 9px; max-width: 860px; padding: 8px 10px; border: 1px solid #E9D5FF; border-radius: 6px; background: #FAF5FF;">
        <div style="font-weight: 700; color: #7C3AED; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; padding-top: 1px;">Coaching</div>
        <div style="color: #4C1D95; line-height: 1.5;">{{ grade.coaching }}</div>
      </div>
    </div>

    <QaRubric
      mode="graded"
      :questions="questions"
      :answers="grade.answers || []"
      :auto-fail-ids="(grade.auto_fails || []).map(f => f.id)"
      empty-text="No rubric answers were returned for this call."
    />
  </div>
</template>

<script>
import QaRubric from './QaRubric.vue';

// One graded call, AI grade or human audit: score, summary, coaching and every answer with its evidence. The QA
// report's call detail, and the template editor's preview of a QA rubric (with a sample grade).
// grade: a row from /cm-qa-scorecards/calls/ (score_pct, earned, applicable, failed, summary, coaching, answers, …).
export default {
  name: 'QaGradedCall',
  components: { QaRubric },
  props: {
    grade: { type: Object, required: true },
    questions: { type: Array, default: () => [] },
  },
  computed: {
    // Yes / No / N-A counts, so the header says how the score was composed without the reader tallying pills.
    answerTally() {
      const answers = this.grade.answers || [];
      if (!answers.length) return '';
      const n = { Yes: 0, No: 0, other: 0 };
      answers.forEach((a) => {
        if (a.answer === 'Yes') n.Yes += 1;
        else if (a.answer === 'No') n.No += 1;
        else n.other += 1;
      });
      const parts = [`${n.Yes} yes`, `${n.No} no`];
      if (n.other) parts.push(`${n.other} n/a`);
      return parts.join(' · ');
    },
  },
  methods: {
    fmtPct(v) {
      return v === null || v === undefined ? '—' : `${v}%`;
    },
    scoreColour(v) {
      if (v === null || v === undefined) return '#A5A5A6';
      if (v >= 85) return '#0E6B43';
      if (v >= 70) return '#353537';
      return '#B45309';
    },
  },
};
</script>

<style scoped>
.qa-graded-call { font-size: 14px; }
.qa-tag {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #E8E9E9;
  border-radius: 9px;
  font-size: 0.86em;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
}
.qa-tag-fail { background: #FEF2F2; color: #B91C1C; border-color: #FECACA; }
</style>
